"use client";

/**
 * NicheCardLanding — landing-v2 adapter around FoilCard for Scene 1.
 *
 * Why not reuse NicheCardV2 directly:
 *   - NicheCardV2 re-computes its revenue + first-sale-day strings from
 *     numeric niche.revenuePotentialMax + niche.timeToFirstRevenueWeeks.
 *     Landing's inputs (DemoNiche.heroStat / MockHookNiche) already carry
 *     pre-formatted strings ("$82k/mo", "day 9") — re-deriving them would
 *     either round incorrectly (1.28 weeks * 7 = 8.999...) or throw away
 *     data that was curated to match voice (SOM vs max revenue).
 *   - NicheCardV2's CTA is hardcoded "Dive →". Landing needs "Read →" for
 *     demo cards and "Dive deeper in full app →" for the interactive reveal.
 *
 * This component keeps the SAME visual shell (FoilCard + illustration +
 * FoilGradeBadgeV2 + amber CTA) but accepts explicit pre-formatted stat
 * strings + configurable CTA label. Zero new design tokens introduced.
 *
 * Motion parity with NicheCardV2:
 *   - Entrance: blur(8px)→0, y 24→0, opacity 0→1, 600ms Apple ease-out
 *   - stagger = index * 120ms
 *   - FoilGradeBadgeV2 spring-pop ~400ms into card entrance (primitive-owned)
 *   - Reduced-motion: flat 200ms fade-in, no stagger
 */

import { motion, useInView, useReducedMotion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { cn } from "@/lib/utils";
import type { NicheVisualMood } from "@/types/niche-v3";
import { FoilCard } from "@/components/niche-v3/shared/FoilCard";
import { FoilGradeBadge as FoilGradeBadgeV2 } from "@/components/shared-primitives/FoilGradeBadge";

const CARD_STAGGER_MS = 120;
const APPLE_EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1];

interface ParsedNumericToken {
  prefix: string;
  target: number;
  suffix: string;
}

function parseNumericToken(value: string): ParsedNumericToken | null {
  const match = value.match(/^(.*?)(\d+(?:\.\d+)?)(.*)$/);
  if (!match) return null;
  const target = Number(match[2]);
  if (!Number.isFinite(target)) return null;

  return {
    prefix: match[1],
    target,
    suffix: match[3],
  };
}

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function useAnimatedInteger({
  target,
  active,
  delayMs,
  reduced,
}: {
  target: number;
  active: boolean;
  delayMs: number;
  reduced: boolean | null;
}) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (reduced || !active) return;

    let frame = 0;
    const durationMs = 820;
    const startAt = performance.now() + delayMs;

    const tick = (now: number) => {
      if (now < startAt) {
        frame = requestAnimationFrame(tick);
        return;
      }

      const progress = Math.min(1, (now - startAt) / durationMs);
      setValue(Math.round(target * easeOutCubic(progress)));

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame((now) => {
      setValue(0);
      tick(now);
    });
    return () => cancelAnimationFrame(frame);
  }, [active, delayMs, reduced, target]);

  if (reduced) return target;
  if (!active) return 0;

  return value;
}

function AnimatedNumericText({
  value,
  active,
  delayMs,
  reduced,
}: {
  value: string;
  active: boolean;
  delayMs: number;
  reduced: boolean | null;
}) {
  const parsed = useMemo(() => parseNumericToken(value), [value]);
  const animated = useAnimatedInteger({
    target: parsed?.target ?? 0,
    active: active && parsed !== null,
    delayMs,
    reduced,
  });

  if (!parsed) return <>{value}</>;

  return (
    <span aria-label={value}>
      {parsed.prefix}
      {animated}
      {parsed.suffix}
    </span>
  );
}

interface NicheCardLandingProps {
  /** Display name — 2-4 words. */
  name: string;
  /** Italic serif hook (line-clamp-3). */
  dreamHook: string;
  /** Letter grade string — "A+" | "A" | "A-" | "B+" | "B" | "B-" accepted. */
  grade: string;
  /** Pre-formatted revenue stat — "$82k/mo". No re-derivation. */
  heroStatRevenue: string;
  /**
   * Lifestyle translation of revenue — "Quit the day job in 4 months."
   * Optional: omitted by the dynamic reveal card (Wave 1 mock endpoint
   * doesn't return it). Rendered italic white/55 directly under revenue.
   */
  dreamTranslation?: string;
  /** Pre-formatted day stat — "day 9". Rendered as "first sale day 9". */
  heroStatDay: string;
  /** Mood for ambient gradient. */
  mood: NicheVisualMood;
  /** True = Top Pick treatment (shimmer + ring + amber glow + chip). */
  isTopPick?: boolean;
  /** CTA button label — default "See how →". */
  ctaLabel?: string;
  /** Click handler — fires on whole-card click AND CTA click. */
  onClick: () => void;
  /** 0-based index for entrance stagger (120ms each). */
  staggerIndex?: number;
  /** aria-label override — default "<ctaLabel> <name>". */
  ariaLabel?: string;
  /**
   * Founder-mirror micro-line under revenue ("Built by founders like you").
   * Optional — omit on dynamic reveal card. white/40, single short line.
   */
  founderMirror?: string;
  /**
   * Top-pick 1-line explainer rendered under the TOP PICK badge.
   * Only rendered when isTopPick === true AND this prop is provided.
   * Single line, amber-300/70, uppercase tracking-wider micro-text.
   */
  topPickExplainer?: string;
}

export function NicheCardLanding({
  name,
  dreamHook,
  grade,
  heroStatRevenue,
  dreamTranslation,
  heroStatDay,
  mood,
  isTopPick = false,
  ctaLabel = "See how →",
  onClick,
  staggerIndex = 0,
  ariaLabel,
  founderMirror,
  topPickExplainer,
}: NicheCardLandingProps) {
  const reduced = useReducedMotion();
  const cardRef = useRef<HTMLDivElement>(null);
  const cardInView = useInView(cardRef, { once: true, margin: "-80px" });
  const countersActive = Boolean(reduced || cardInView);

  const initial = reduced
    ? false
    : { opacity: 0, y: 24, filter: "blur(8px)" };
  const animate = { opacity: 1, y: 0, filter: "blur(0px)" };
  const transition = reduced
    ? { duration: 0.2, ease: "easeOut" as const }
    : {
        duration: 0.6,
        ease: APPLE_EASE_OUT,
        delay: (staggerIndex * CARD_STAGGER_MS) / 1000,
      };

  const effectiveAriaLabel = ariaLabel ?? `${ctaLabel.replace(/[→\s]+$/, "")} ${name}`;

  return (
    <motion.div
      ref={cardRef}
      initial={initial}
      animate={animate}
      transition={transition}
      whileTap={reduced ? undefined : { scale: 0.98 }}
      className="h-full w-full max-w-[360px]"
    >
      <FoilCard
        mood={mood}
        topPick={isTopPick}
        onClick={onClick}
        ariaLabel={effectiveAriaLabel}
        className="h-full min-h-[380px]"
      >
        {/* Hero-art zone — h-20 — TopPick chip top-left, grade badge top-right.
         * Shrunk from h-44 after illustration was removed — empty space was
         * pulling the grade badge visually close to the card title below. */}
        <div className="relative h-24 shrink-0 overflow-visible sm:h-20">
          {isTopPick && (
            <div className="absolute left-4 right-24 top-4 z-10 flex flex-col gap-1.5 sm:right-20">
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full self-start",
                  "border border-amber-400/30 bg-amber-400/15 px-2.5 py-1",
                  "text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-300",
                )}
              >
                <Sparkles className="size-3" aria-hidden />
                Top Pick
              </span>
              {topPickExplainer && (
                <motion.span
                  initial={reduced ? false : { opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={
                    reduced
                      ? { duration: 0 }
                      : {
                          duration: 0.2,
                          ease: APPLE_EASE_OUT,
                          delay:
                            (staggerIndex * CARD_STAGGER_MS) / 1000 + 0.45,
                        }
                  }
                  className={cn(
                    "self-start text-[11px] uppercase tracking-wider",
                    "leading-[1.35] text-amber-300/70",
                  )}
                >
                  {topPickExplainer}
                </motion.span>
              )}
            </div>
          )}

          {/* Grade badge — top-right INSIDE safe area (was -top-1 -right-1
           * sticker-effect which FoilCard's overflow-hidden clipped). Badge is
           * INFORMATION (tier signal), must be fully visible over readability. */}
          <div className="absolute right-4 top-4 z-10 sm:hidden">
            <FoilGradeBadgeV2 grade={grade} size="sm" animateOnMount={true} />
          </div>
          <div className="absolute right-3 top-3 z-10 hidden sm:block">
            <FoilGradeBadgeV2 grade={grade} size="md" animateOnMount={true} />
          </div>
        </div>

        {/* Content zone */}
        <div className="flex flex-1 flex-col p-6 pt-2">
          <h3
            className={cn(
              "font-heading font-bold text-white leading-[1.1]",
              "text-[26px] line-clamp-2",
            )}
          >
            {name}
          </h3>

          <p
            className={cn(
              "mt-3 font-heading italic leading-[1.5]",
              "text-[16px] text-white/85 line-clamp-3",
            )}
          >
            {dreamHook}
          </p>

          {/* Hero stat + CTA row (pinned to bottom via mt-auto) */}
          <div className="mt-auto pt-5">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-heading text-[20px] font-semibold tabular-nums text-amber-400">
                <AnimatedNumericText
                  value={heroStatRevenue}
                  active={countersActive}
                  delayMs={staggerIndex * CARD_STAGGER_MS + 220}
                  reduced={reduced}
                />
              </span>
              <span className="text-[13px] text-white/50">
                first sale{" "}
                <AnimatedNumericText
                  value={heroStatDay}
                  active={countersActive}
                  delayMs={staggerIndex * CARD_STAGGER_MS + 340}
                  reduced={reduced}
                />
              </span>
            </div>

            {dreamTranslation && (
              <motion.p
                initial={reduced ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={
                  reduced
                    ? { duration: 0 }
                    : {
                        duration: 0.2,
                        ease: APPLE_EASE_OUT,
                        delay:
                          (staggerIndex * CARD_STAGGER_MS) / 1000 + 0.5,
                      }
                }
                className={cn(
                  "mt-1.5 font-heading italic",
                  "text-[14px] leading-snug text-white/55",
                )}
              >
                {dreamTranslation}
              </motion.p>
            )}

            {founderMirror && (
              <motion.p
                initial={reduced ? false : { opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={
                  reduced
                    ? { duration: 0 }
                    : {
                        duration: 0.2,
                        ease: APPLE_EASE_OUT,
                        delay:
                          (staggerIndex * CARD_STAGGER_MS) / 1000 + 0.6,
                      }
                }
                className={cn(
                  "mt-2 text-[12px] uppercase tracking-[0.14em]",
                  "text-white/40",
                )}
              >
                {founderMirror}
              </motion.p>
            )}

            <button
              type="button"
              data-foil-card-cta-stop
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                onClick();
              }}
              className={cn(
                "mt-4 flex h-11 w-full items-center justify-center rounded-xl",
                "bg-amber-400 text-zinc-900 font-semibold",
                "transition-all duration-150",
                "hover:bg-amber-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-900",
                "active:scale-[0.98]",
              )}
              aria-label={effectiveAriaLabel}
            >
              <span className="font-heading">{ctaLabel}</span>
            </button>
          </div>
        </div>
      </FoilCard>
    </motion.div>
  );
}
